import React, { memo, useMemo } from 'react';

import { ChatSuggestedEvent, UnifiedChatRichContextEntry } from '../../../data-access';
import type { NearbyPlace } from '../../../data-access/api/science/openapi/api';
import type { ProjectFilterItem } from '../../../hooks';
import './AssistantChatSuggestedBlocks.scss';
import AssistantChatSuggestedEventBlock from './AssistantChatSuggestedEventBlock';
import AssistantChatSuggestedTaskBlock from './AssistantChatSuggestedTaskBlock';
import AssistantChatSuggestedRoutineBlock from './AssistantChatSuggestedRoutineBlock';
import SuggestionGroupCard from '../SuggestedBlocks/SuggestionGroupCard';
import { sortSuggestedBlocksByDate } from './helpers';

// Stable fallback reference — `?? []` would allocate a new array every render,
// which breaks memo() on every block that has no nearby places.
const EMPTY_NEARBY_PLACES: NearbyPlace[] = [];
const EMPTY_RICH_CONTEXT_ENTRIES: UnifiedChatRichContextEntry[] = [];

const TASK_ACTIONS = new Set(['setTask', 'deleteTask']);
const ROUTINE_ACTIONS = new Set(['setRoutine', 'deleteRoutine']);

interface AssistantChatSuggestedBlocksProps {
  blocks: ChatSuggestedEvent[];
  loadingEventId?: string;
  processedIds: string[];
  onProcessedIdsChange: (ids: string[]) => void;
  onManageCalendar?: (e: ChatSuggestedEvent) => Promise<boolean>;
  onCheckCalendar?: (startTime: string) => void;
  onManageTodo?: (e: ChatSuggestedEvent, board: ProjectFilterItem) => Promise<boolean>;
  nearbyPlacesByEventId?: Record<string, NearbyPlace[]>;
  /** 'adjustment'-sourced richContext entries matched to the block they
   * belong to (see buildRichContextEntriesByEventId) — rendered inside that
   * block's own card rather than as a separate chat bubble. */
  richContextEntriesByEventId?: Record<string, UnifiedChatRichContextEntry[]>;
  /** 'screen' renders the grouped SuggestionGroupCard (Approve All/Dismiss
   * All bar + per-item Accept/Discard rows) instead of the plain chat-feed
   * list — used by PlanPreviewBubble. */
  variant?: 'default' | 'screen';
  onDiscardBlock?: (eventId: string) => void;
  onDiscardAllBlocks?: (eventIds: string[]) => void;
  /** Screen variant only: block ids accepted via auto-accept settings. */
  autoAcceptedIds?: Set<string>;
}

const AssistantChatSuggestedBlocks: React.FC<AssistantChatSuggestedBlocksProps> = memo(
  ({
    blocks,
    loadingEventId,
    processedIds,
    onProcessedIdsChange,
    onManageCalendar = () => Promise.resolve(false),
    onCheckCalendar,
    onManageTodo = () => Promise.resolve(false),
    nearbyPlacesByEventId = {},
    richContextEntriesByEventId = {},
    variant = 'default',
    onDiscardBlock,
    onDiscardAllBlocks,
    autoAcceptedIds,
  }) => {
    const sortedBlocks = useMemo(() => sortSuggestedBlocksByDate(blocks), [blocks]);

    if (variant === 'screen') {
      return (
        <SuggestionGroupCard
          blocks={sortedBlocks}
          loadingEventId={loadingEventId}
          processedIds={processedIds}
          onProcessedIdsChange={onProcessedIdsChange}
          onManageCalendar={onManageCalendar}
          onCheckCalendar={onCheckCalendar}
          onManageTodo={onManageTodo}
          nearbyPlacesByEventId={nearbyPlacesByEventId}
          richContextEntriesByEventId={richContextEntriesByEventId}
          onDiscardBlock={onDiscardBlock}
          onDiscardAllBlocks={onDiscardAllBlocks}
          autoAcceptedIds={autoAcceptedIds}
        />
      );
    }

    const renderBlock = (block: ChatSuggestedEvent) => {
      const key = block.linkedMessageId || block.eventId;

      if (TASK_ACTIONS.has(block.action || '')) {
        return (
          <AssistantChatSuggestedTaskBlock
            key={key}
            block={block}
            loadingEventId={loadingEventId}
            processedIds={processedIds}
            onProcessedIdsChange={onProcessedIdsChange}
            onManageTodo={onManageTodo}
          />
        );
      }

      if (ROUTINE_ACTIONS.has(block.action || '')) {
        return (
          <AssistantChatSuggestedRoutineBlock
            key={key}
            block={block}
            loadingEventId={loadingEventId}
            processedIds={processedIds}
            onProcessedIdsChange={onProcessedIdsChange}
            onManageCalendar={onManageCalendar}
          />
        );
      }

      return (
        <AssistantChatSuggestedEventBlock
          key={key}
          block={block}
          loadingEventId={loadingEventId}
          processedIds={processedIds}
          onProcessedIdsChange={onProcessedIdsChange}
          onManageCalendar={onManageCalendar}
          onCheckCalendar={onCheckCalendar}
          nearbyPlaces={nearbyPlacesByEventId[block.eventId] ?? EMPTY_NEARBY_PLACES}
          richContextEntries={
            richContextEntriesByEventId[block.eventId] ?? EMPTY_RICH_CONTEXT_ENTRIES
          }
        />
      );
    };

    return <div className="assistant-chat-suggested-blocks">{sortedBlocks.map(renderBlock)}</div>;
  }
);

export default AssistantChatSuggestedBlocks;
